/**
 * useOfflinePrescription - Hook para operações de receita com suporte offline
 */

import { useCallback } from 'react';
import { useOffline } from '@/contexts/OfflineContext';
import { 
  createPrescriptionLocal, 
  updatePrescriptionLocal,
  getPrescriptionsByPatient 
} from '@/services/indexedDB';
import axiosInstance from '@/utils/axios';
import { toast } from 'sonner';

export function useOfflinePrescription() {
  const { isOnline, isOfflineReady, refreshLocalStats } = useOffline();

  /**
   * Criar receita - funciona online e offline
   * Nota: Offline apenas cria RASCUNHO (draft)
   */
  const createPrescription = useCallback(async (prescriptionData, patientId) => {
    // Verificar se IndexedDB está pronto para fallback
    const canFallbackToLocal = isOfflineReady;
    
    // Se paciente é local, só pode criar receita local
    if (patientId && patientId.startsWith('local_')) {
      console.log('[OfflinePrescription] Patient is local, creating local draft');
      return await savePrescriptionLocally(prescriptionData, patientId);
    }
    
    // Se está offline, salva como rascunho local direto
    if (!navigator.onLine) {
      console.log('[OfflinePrescription] Offline detected, saving as draft');
      if (!canFallbackToLocal) {
        throw new Error('Modo offline não disponível. Aguarde o sistema inicializar.');
      }
      return await savePrescriptionLocally(prescriptionData, patientId);
    }

    // Se online, tenta criar no servidor (receita final)
    try {
      const response = await axiosInstance.post('/prescriptions', {
        patient_id: patientId,
        ...prescriptionData
      });
      return {
        success: true,
        prescription: response.data,
        isLocal: false,
        isDraft: false
      };
    } catch (error) {
      console.log('[OfflinePrescription] API error:', error.message, error.code);
      
      // Se falhar por erro de rede, salva como rascunho
      const isNetworkError = 
        error.code === 'ERR_NETWORK' || 
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !navigator.onLine;
      
      if (isNetworkError && canFallbackToLocal) {
        console.log('[OfflinePrescription] Network error, saving as draft');
        return await savePrescriptionLocally(prescriptionData, patientId);
      }
      
      throw error;
    }
  }, [isOfflineReady]);

  /**
   * Salvar receita como rascunho local (IndexedDB)
   */
  const savePrescriptionLocally = async (prescriptionData, patientId) => {
    if (!isOfflineReady) {
      throw new Error('IndexedDB não está pronto. Tente novamente em alguns segundos.');
    }

    try {
      // Determinar se é paciente local ou do servidor
      const isLocalPatient = patientId && patientId.startsWith('local_');
      
      const localPrescription = await createPrescriptionLocal({
        patient_local_id: isLocalPatient ? patientId : null,
        patient_server_id: isLocalPatient ? null : patientId,
        medicamentos: prescriptionData.medicamentos || [],
        uso: prescriptionData.uso || 'USO ORAL',
        data_validade: prescriptionData.data_validade,
        medication_groups: prescriptionData.medication_groups
      });

      await refreshLocalStats();

      toast.warning('Receita salva como RASCUNHO', {
        duration: 5000,
        description: 'Será finalizada quando houver conexão. Rascunhos não têm validade legal.'
      });

      return {
        success: true,
        prescription: {
          id: localPrescription.local_id,
          patient_id: patientId,
          medicamentos: localPrescription.medicamentos,
          uso: localPrescription.uso,
          data_emissao: localPrescription.data_emissao,
          status: 'draft',
          isLocal: true,
          isDraft: true,
          sync_status: localPrescription.sync_status
        },
        isLocal: true,
        isDraft: true
      };
    } catch (error) {
      console.error('[OfflinePrescription] Error saving locally:', error);
      throw error;
    }
  };

  /**
   * Atualizar receita local (rascunho)
   */
  const updatePrescriptionDraft = useCallback(async (localId, updateData) => {
    if (!localId.startsWith('local_')) {
      throw new Error('Apenas rascunhos locais podem ser atualizados offline');
    }

    try {
      const updated = await updatePrescriptionLocal(localId, updateData);
      await refreshLocalStats();

      return {
        success: true,
        prescription: updated,
        isLocal: true,
        isDraft: true
      };
    } catch (error) {
      console.error('[OfflinePrescription] Error updating draft:', error);
      throw error;
    }
  }, [refreshLocalStats]);

  /**
   * Obter receitas do paciente (local + servidor se online)
   */
  const getPatientPrescriptions = useCallback(async (patientId) => {
    const results = {
      serverPrescriptions: [],
      localDrafts: []
    };

    // Buscar rascunhos locais
    if (isOfflineReady) {
      try {
        const localPrescriptions = await getPrescriptionsByPatient(patientId);
        results.localDrafts = localPrescriptions.map(p => ({
          ...p,
          isLocal: true,
          isDraft: true
        }));
      } catch (error) {
        console.error('[OfflinePrescription] Error getting local drafts:', error);
      }
    }

    // Se online, buscar do servidor também
    if (isOnline && !patientId.startsWith('local_')) {
      try {
        const response = await axiosInstance.get(`/patients/${patientId}/prescriptions`);
        results.serverPrescriptions = response.data.map(p => ({
          ...p,
          isLocal: false,
          isDraft: false
        }));
      } catch (error) {
        console.error('[OfflinePrescription] Error getting server prescriptions:', error);
      }
    }

    return results;
  }, [isOnline, isOfflineReady]);

  /**
   * Finalizar rascunho (requer conexão)
   */
  const finalizeDraft = useCallback(async (localId) => {
    if (!isOnline) {
      toast.error('É necessário conexão com a internet para finalizar a receita.');
      return { success: false, error: 'offline' };
    }

    // TODO: Implementar lógica de finalização quando necessário
    toast.info('Funcionalidade de finalização em desenvolvimento.');
    return { success: false, error: 'not_implemented' };
  }, [isOnline]);

  /**
   * Verificar se pode criar receita final (requer conexão)
   */
  const canCreateFinalPrescription = useCallback(() => {
    return isOnline;
  }, [isOnline]);

  return {
    createPrescription,
    updatePrescriptionDraft,
    getPatientPrescriptions,
    finalizeDraft,
    canCreateFinalPrescription,
    isOnline,
    isOfflineReady
  };
}

export default useOfflinePrescription;
